import { CheckCircleOutlined, ClockCircleOutlined, MinusCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { L } from '@lib';
import { PurchaseOrder, PurchaseOrderStatus } from '@services';
import { Button, Space, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  [PurchaseOrderStatus.Draft]: { color: 'blue', icon: <ClockCircleOutlined />, label: 'Draft' },
  [PurchaseOrderStatus.Ordered]: { color: 'orange', icon: <SyncOutlined />, label: 'Ordered' },
  [PurchaseOrderStatus.Received]: { color: 'green', icon: <CheckCircleOutlined />, label: 'Received' },
  [PurchaseOrderStatus.Canceled]: { color: 'default', icon: <MinusCircleOutlined />, label: 'Canceled' },
};

interface IProps {
  onEdit: (item: PurchaseOrder) => void;
  onMarkOrdered: (item: PurchaseOrder) => void;
  onReceive: (item: PurchaseOrder) => void;
  onCancel: (item: PurchaseOrder) => void;
}

export const PurchaseOrderColumns = ({ onEdit, onMarkOrdered, onReceive, onCancel }: IProps) => [
  {
    title: L('Part'),
    key: 'part',
    width: 200,
    ellipsis: true,
    render: (_: unknown, record: PurchaseOrder) => (
      <div>
        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.partName}</div>
        {(record.partNumber || record.reference) && (
          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {record.partNumber && <span>#{record.partNumber}</span>}
            {record.partNumber && record.reference && <span style={{ margin: '0 4px' }}>·</span>}
            {record.reference && <span>{record.reference}</span>}
          </div>
        )}
        {record.registrationNumber && (
          <div style={{ fontSize: 11, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {record.registrationNumber}{record.vin && ` · ${record.vin}`}
          </div>
        )}
      </div>
    ),
  },
  {
    title: L('Supplier'),
    dataIndex: 'supplierName',
    key: 'supplierName',
    width: 130,
    ellipsis: true,
    render: (val: string | null) => val ?? <span style={{ color: '#bfbfbf' }}>—</span>,
  },
  {
    title: L('Qty'),
    dataIndex: 'quantity',
    key: 'quantity',
    width: 55,
  },
  {
    title: L('Received'),
    dataIndex: 'receivedQuantity',
    key: 'receivedQuantity',
    width: 75,
    render: (val: number | null) => val ?? <span style={{ color: '#bfbfbf' }}>—</span>,
  },
  {
    title: L('Status'),
    dataIndex: 'status',
    key: 'status',
    width: 105,
    render: (val: string) => {
      const cfg = statusConfig[val];
      return cfg ? (
        <Tag color={cfg.color} icon={cfg.icon} style={{ margin: 0 }}>
          {cfg.label}
        </Tag>
      ) : (
        <Tag style={{ margin: 0 }}>{val}</Tag>
      );
    },
  },
  {
    title: L('Origin'),
    key: 'origin',
    width: 110,
    render: (_: unknown, record: PurchaseOrder) => {
      if (record.buildReference) {
        return (
          <Tag
            color="purple"
            style={{ margin: 0, cursor: record.masterBuildId ? 'pointer' : 'default', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}
            onClick={record.masterBuildId ? (e) => { e.stopPropagation(); window.open(`/master-builds/${record.masterBuildId}`, '_blank'); } : undefined}
          >
            {record.buildReference}
          </Tag>
        );
      }
      if (record.orderId) {
        return (
          <Tag
            color="geekblue"
            style={{ margin: 0, cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); window.open(`/orders/${record.orderId}`, '_blank'); }}
          >
            Job card
          </Tag>
        );
      }
      return <Tag style={{ margin: 0 }}>Stock</Tag>;
    },
  },
  {
    title: L('Date'),
    dataIndex: 'creationTime',
    key: 'creationTime',
    width: 80,
    render: (val: string) => dayjs(val).format('DD MMM YY'),
  },
  {
    title: '',
    key: 'action',
    width: 200,
    render: (_: unknown, record: PurchaseOrder) => (
      <Space size={4} onClick={(e) => e.stopPropagation()}>
        <Button size="small" onClick={() => onEdit(record)}>Edit</Button>
        {record.status === PurchaseOrderStatus.Draft && (
          <Tooltip title={record.supplierId ? undefined : L('Assign a supplier first')}>
            <Button
              size="small"
              type="primary"
              ghost
              disabled={!record.supplierId}
              onClick={() => onMarkOrdered(record)}
            >
              Mark Ordered
            </Button>
          </Tooltip>
        )}
        {record.status === PurchaseOrderStatus.Ordered && (
          <Button size="small" type="primary" ghost onClick={() => onReceive(record)}>
            Receive
          </Button>
        )}
        {(record.status === PurchaseOrderStatus.Draft || record.status === PurchaseOrderStatus.Ordered) && (
          <Button size="small" danger onClick={() => onCancel(record)}>
            Cancel
          </Button>
        )}
      </Space>
    ),
  },
];

